import React from "react";
import { useNavigate } from "react-router-dom";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { useCart } from "@/context/CartContext";
import { formatINR, sameOriginMedia } from "@/lib/api";
import { Minus, Plus, Trash2, ShoppingBag, ArrowRight, Heart } from "lucide-react";

export const CartDrawer = () => {
  const { items, isOpen, setIsOpen, updateQty, removeItem, subtotal, count } = useCart();
  const navigate = useNavigate();

  const goCheckout = () => {
    setIsOpen(false);
    navigate("/checkout");
  };

  const goPersonalize = () => {
    setIsOpen(false);
    navigate("/personalize");
  };

  return (
    <Sheet open={isOpen} onOpenChange={setIsOpen}>
      <SheetContent side="right" className="flex w-full flex-col gap-0 bg-wp-bg p-0 sm:max-w-md" data-testid="cart-drawer">
        <SheetHeader className="glass-strong border-b border-border px-5 py-4">
          <SheetTitle className="flex items-center gap-2 font-display text-xl">
            <ShoppingBag className="h-5 w-5 text-wp-rose-strong" /> Your Parcel Cart
          </SheetTitle>
        </SheetHeader>

        <div className="flex-1 overflow-y-auto px-5 py-4">
          {items.length === 0 ? (
            <div className="flex h-full flex-col items-center justify-center text-center">
              <div className="grid h-20 w-20 place-items-center rounded-full bg-wp-blush-100">
                <Gift />
              </div>
              <p className="mt-4 font-display text-lg">Your cart is feeling empty</p>
              <p className="mt-1 text-sm text-wp-ink-muted">Add a gift-ready parcel and we’ll pack it beautifully.</p>
              <Button className="mt-5 rounded-xl" onClick={() => { setIsOpen(false); navigate("/shop"); }} data-testid="cart-empty-shop-button">
                Shop Gift Boxes
              </Button>
            </div>
          ) : (
            <ul className="space-y-3">
              {items.map((it) => (
                <li key={`${it.type}-${it.id}`} className="flex gap-3 rounded-wp-md border border-border bg-card p-3 shadow-wp-soft" data-testid={`cart-item-${it.id}`}>
                  <div className="h-20 w-20 overflow-hidden rounded-wp-sm bg-wp-cream-100">
                    {it.image ? <img src={sameOriginMedia(it.image)} alt={it.name} loading="lazy" className="h-full w-full object-cover" /> : null}
                  </div>
                  <div className="flex flex-1 flex-col">
                    <div className="flex items-start justify-between gap-2">
                      <p className="text-sm font-semibold leading-tight">{it.name}</p>
                      <button onClick={() => removeItem(it.id, it.type)} aria-label="Remove" data-testid={`cart-remove-${it.id}`} className="text-wp-ink-muted hover:text-destructive">
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                    <span className="price-num mt-0.5 text-sm font-semibold text-wp-rose-strong">{formatINR(it.price)}</span>
                    <div className="mt-auto flex items-center gap-2" data-testid="cart-item-quantity-stepper">
                      <button onClick={() => updateQty(it.id, it.type, it.qty - 1)} className="grid h-7 w-7 place-items-center rounded-full border border-border hover:bg-wp-blush-100"><Minus className="h-3.5 w-3.5" /></button>
                      <span className="price-num w-6 text-center text-sm">{it.qty}</span>
                      <button onClick={() => updateQty(it.id, it.type, it.qty + 1)} className="grid h-7 w-7 place-items-center rounded-full border border-border hover:bg-wp-blush-100"><Plus className="h-3.5 w-3.5" /></button>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        {items.length > 0 && (
          <div className="glass-strong border-t border-border px-5 py-4">
            <div className="mb-3 flex items-center justify-between">
              <span className="text-sm text-wp-ink-muted">Subtotal ({count} item{count > 1 ? "s" : ""})</span>
              <span className="price-num text-lg font-semibold text-wp-rose-strong">{formatINR(subtotal)}</span>
            </div>
            <p className="mb-3 text-xs text-wp-ink-muted">✨ Gift-ready packaging &amp; a handwritten note included free.</p>
            <button onClick={goPersonalize} data-testid="cart-make-personal-button"
              className="mb-3 flex w-full items-center gap-3 rounded-wp-lg border border-wp-blush-200 bg-wp-blush-50 px-4 py-3 text-left transition-colors hover:border-wp-rose-strong">
              <span className="grid h-9 w-9 flex-shrink-0 place-items-center rounded-full bg-white text-wp-rose-strong shadow-wp-soft"><Heart className="h-4 w-4" /></span>
              <span className="flex-1">
                <span className="block text-sm font-semibold text-wp-ink">Make it personal</span>
                <span className="block text-xs text-wp-ink-muted">Add a photo, a voice note &amp; a heartfelt message</span>
              </span>
              <ArrowRight className="h-4 w-4 text-wp-rose-strong" />
            </button>
            <Button onClick={goCheckout} className="w-full rounded-xl bg-wp-rose-strong text-white hover:brightness-105" data-testid="cart-drawer-checkout-button">
              Checkout <ArrowRight className="ml-1 h-4 w-4" />
            </Button>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
};

const Gift = () => (
  <svg width="34" height="34" viewBox="0 0 24 24" fill="none" stroke="hsl(var(--wp-rose-gold-strong))" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="8" width="18" height="4" rx="1"/><path d="M12 8v13M19 12v7a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2v-7"/><path d="M7.5 8a2.5 2.5 0 0 1 0-5C11 3 12 8 12 8S13 3 16.5 3a2.5 2.5 0 0 1 0 5"/></svg>
);
